import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { ChevronRightIcon, ChevronLeftIcon, PlusIcon, CheckIcon } from '@heroicons/react/solid';
import { CheckCircleIcon } from '@heroicons/react/solid';
// import { DocumentTextIcon, CheckCircleIcon, MicrophoneIcon, StopIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid';
import DocumentPreviewPanel from './components/DocumentPreviewPanel';
import { auth } from '../../../firebase';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import DictationTextArea from '../../../components/DictationTextArea';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ShouldDisplayElement } from '../../../utils/ShouldDisplayElement';
import InfoTooltip from '../../../components/InfoTooltip';
import { Tooltip } from 'react-tooltip';
import { SearchIcon, ClockIcon, XIcon } from '@heroicons/react/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { formatDateTime } from '../../../utils/timestampUtils';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../utils/formDisplayUtils';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import { getDifferentialDiagnoses } from '../../../utils/diagnosticDecisionRouting';
import AutismDecisionCard from '../../../components/autismReview/AutismDecisionCard';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  rewriteSubsectionTitleDisplay,
  readIsCarryOutHealthChecksNow,
  buildPadSidebarUnlockFlags,
} from '../../../utils/padNavigationUtils';
import {
  findCiAssessmentGoalsQuestion,
  resolveAssessmentGoalsDisplay,
  resolvePadGoalsClinicianNotes,
  ASSESSMENT_GOALS_SERVICE_USER_VAR,
} from '../../../utils/mdtPlanUtils';
import PadRedoArchiveBanner from '../../../components/PadRedoArchiveBanner';

export default function PADReviewDiagnosis() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const { usesNextStepsPad, isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false, skipDifferentialsDecisions = false } = useWorkflowConfig(patientDataModel, claims);
  const [hasChanges, setHasChanges] = useState(false);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode — interactive elements
  // are disabled and section-completion / AI-generation calls are skipped.
  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Next/Finish/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Post-Assessment Discussion',
    sectionTitle: 'Review of Assessment',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // Add form state management
  const [formState, setFormState] = useState({});

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add these handler functions near your other handler functions
  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error completing section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = async () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  };

  // Speech recognition states
  // const [activeRecording, setActiveRecording] = useState(null);
  // const [recognition, setRecognition] = useState(null);
  const textAreaRefs = useRef({});

  // // Speech recognition handlers
  // const handleRecording = (subsectionId) => {
  //   if (!('webkitSpeechRecognition' in window)) {
  //     alert('Speech recognition not supported in this browser.');
  //     return;
  //   }

  //   if (recognition) {
  //     recognition.stop();
  //     return;
  //   }

  //   const newRecognition = new window.webkitSpeechRecognition();
  //   newRecognition.continuous = true;
  //   newRecognition.interimResults = true;
  //   newRecognition.lang = 'en-GB';

  //   newRecognition.onresult = (event) => {
  //     let finalTranscript = notes[subsectionId] || '';
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       let transcript = event.results[i][0].transcript.trim();
  //       if (event.results[i].isFinal) {
  //         transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
  //         finalTranscript += transcript;
  //       }
  //     }
  //     handleNotesChange(subsectionId, finalTranscript);
  //   };

  //   newRecognition.onend = () => {
  //     setActiveRecording(null);
  //     setRecognition(null);

  //     // Simulate focus and blur to trigger onBlur
  //     const currentTextArea = textAreaRefs.current[subsectionId];
  //     if (currentTextArea) {
  //       currentTextArea.focus();
  //       setTimeout(() => {
  //         currentTextArea.blur();
  //       }, 0);
  //     }
  //   };

  //   newRecognition.start();
  //   setRecognition(newRecognition);
  //   setActiveRecording(subsectionId);

  //   // Focus the text area when recording starts
  //   const currentTextArea = textAreaRefs.current[subsectionId];
  //   if (currentTextArea) {
  //     currentTextArea.focus();
  //   }
  // };

  // Panel states 
  const [notes, setNotes] = useState({});

  // Tracks whether the goals textbox was prefilled from the MDT Plan notes
  // (shown as a dismissible banner, fades on first edit — RSK.js pattern).
  const [showMdtPlanPrefillBanner, setShowMdtPlanPrefillBanner] = useState(false);
  // True when this session copied MDT Plan notes into the PAD goals field
  // for the first time. Generation reads PAD clinicianNotes, not the MDT
  // Plan path, so we must persist that copy and treat it as a change even
  // if the clinician never focuses the box (no blur → no write).
  const mdtGoalsHydratedThisSession = useRef(false);
  const notesRef = useRef({});

  
  // isTOC gates the simplified two-page PAD ("Assessment Outcome" ->
  // "Next Steps") vs the standard multi-section DIS/TRT flow. Driven by
  // padFlow.structure rather than assessmentWorkflowType directly, so a
  // tocAdhd org can opt into the full PAD (e.g. LMP) via an explicit stamp.
  const isTOC = usesNextStepsPad;

  // Constants — display titles adapt based on whether this is a TOC patient.
  // The internal section key stays 'Review of Assessment' for backend compatibility
  // (LLM generation triggers and Firestore stageSectionsGenerated checks).
  const currentStageTitle = 'Post-Assessment Discussion';
  const currentSectionNumber = 1;
  const currentSectionKey = 'Review of Assessment';
  const currentSectionTitle = isTOC ? 'Assessment Outcome' : 'Review of Assessment';
  const prevSectionLink = '/';
  const prevSectionTitle = 'Patient Overview';

  // Ordered stage list for the clinician PAD — controls sidebar order, display
  // numbers, and prev/next links. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Navigation links derived from the ordered stage list. For Review (key '1'),
  // prev is always null (Patient Overview is handled separately), and next
  // points to whichever stage follows Review in the current order.
  const padNav = useMemo(
    () => getPadNavLinks('1', padStageOrder),
    [padStageOrder]
  );

  // TOC uses its own simplified navigation; standard PAD uses the helper.
  const nextSectionLink = isTOC
    ? '/proforma/post-assessment-discussion/next-steps'
    : padNav.nextLink;
  const nextSectionTitle = isTOC ? 'Next Steps' : padNav.nextTitle;

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

 const [openDialogIndex, setOpenDialogIndex] = useState(null);
 const [rawPrefillData, setRawPrefillData] = useState([]);
 const [dialogPrefillText, setDialogPrefillText] = useState('');

  // Resolve assessment goals for the read-only prefill row. On SLaM Path 1 the
  // clinician may reach PAD before opening Presenting Complaint, so we cannot
  // rely on the CI subsectionQuestions node existing — fall back to formData.
  const assessmentGoalsDisplay = useMemo(
    () => resolveAssessmentGoalsDisplay({
      dataModel: patientDataModel?.dataModel,
      formData: patientDataModel?.formData,
    }),
    [patientDataModel?.dataModel, patientDataModel?.formData],
  );

 //   // Update this function to fetch raw prefill data when dialog opens
  const handleOpenRawPrefillDialog = () => {
    const question = findCiAssessmentGoalsQuestion(patientDataModel?.dataModel);
    const varNames = question?.prefilledByServiceUserVarNames?.length
      ? question.prefilledByServiceUserVarNames
      : [ASSESSMENT_GOALS_SERVICE_USER_VAR];

    if (varNames.length > 0 && question && !question.clinicianInput) {
      const rawPrefillData = getRawPrefillInputs(varNames);
      setRawPrefillData(rawPrefillData);
      setDialogPrefillText(question.patientPrefill || assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
      return;
    }

    if (question) {
      setRawPrefillData([{
        formName: question.clinicianInput ? "Clinician Input" : "Your Current Concerns",
        question: question.title,
        answer: question.clinicianInput || question.patientPrefill || assessmentGoalsDisplay.displayText || "No previous answer given",
        hasAnswer: !!(question.clinicianInput || question.patientPrefill || assessmentGoalsDisplay.hasPatientSelfReport),
        lastUpdated: question.clinicianInputUpdatedAt || question.patientPrefillLastUpdatedAt,
        updatedBy: question.clinicianInput ? (question.clinicianInputUpdatedByName || "Unknown clinician") : null
      }]);
      setDialogPrefillText(question.patientPrefill || assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
      return;
    }

    const rawPrefillData = getRawPrefillInputs(varNames);
    if (rawPrefillData.length > 0) {
      setRawPrefillData(rawPrefillData);
      setDialogPrefillText(assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
    }
  };

   // Function to extract raw prefill inputs from formData
  const getRawPrefillInputs = (varNames) => {
    if (!patientDataModel?.formData?.yourCurrentConcerns?.formSubsections?.[1]?.questions ||
      !varNames || varNames.length === 0) {
      return [];
    }

    const results = [];
    const aboutYourReferralQuestions = patientDataModel.formData.yourCurrentConcerns.formSubsections[1].questions;
    
    // Go through all questions in "Your Current Concerns" form
    Object.entries(aboutYourReferralQuestions).forEach(([questionKey, question]) => {
      // Check each subquestion
      if (question.subquestions) {
        Object.entries(question.subquestions).forEach(([subquestionKey, subquestion]) => {
          // Skip non-numbered subquestion keys
          if (isNaN(parseInt(subquestionKey))) return;

          // If this subquestion's variable name is in our list
          if (varNames.includes(subquestion.serviceUserVarName)) {
            const normalized = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
            const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];

            results.push({
              formName: "Your Current Concerns",
              questionTitle: question.questionTitle,
              question: subquestion.subquestionDisplayText,
              answer: buildDisplayAnswer(normalized),
              hasAnswer: normalized !== null && normalized !== undefined && normalized !== '',
              lastUpdated: question.lastUpdated,
              updatedBy: null,
              inputType: subquestion.inputType,
              options: allOptions,
              rawAnswer: normalized,
              otherOptions: subquestion.otherOptions || {},
              wasSkipped: question.wasSkipped,
              skipButtonText: question.skipButtonText
            });
          }
        });
      }
    });

    return results;
  };

  // Add this function to check if an option is selected
  const isOptionSelected = (option, rawAnswer) => {
    if (!rawAnswer) return false;
    
    // For simple string match
    if (typeof rawAnswer === 'string') {
      return rawAnswer === option;
    }
    
    // For array of strings
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'string') {
      return rawAnswer.includes(option);
    }
    
    // For array of objects with value property
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'object') {
      return rawAnswer.some(ans => ans.value === option);
    }
    
    // For single object with value property
    if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) {
      return rawAnswer.value === option;
    }
    
    return false;
  };

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;

    const initialFormState = {};
    const initialNotes = {};

    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize notes if they exist
      if (subsection?.clinicianNotes) {
        initialNotes[subsectionId] = subsection.clinicianNotes;
      }

      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            initialFormState[question.firestoreFieldName] = {
              value: question?.answer?.value || []
            };
          }
        });
      }
    });

    // MDT Plan goals: the box is shown from MDT notes when PAD clinicianNotes
    // has never been written. Showing the text in React state is not enough —
    // generation reads the PAD field, and blur only fires if the box is
    // focused. Persist the copy immediately so an untouched prefill still
    // reaches the goals paragraph. An explicit empty string (clinician
    // cleared the box) is a written value and must not be overwritten.
    const storedPadGoalsNotes = sectionData.subsections?.['2']?.clinicianNotes;
    const mdtPlanGoalsNotes = patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan?.patientGoalsNotes || '';
    const resolvedGoals = resolvePadGoalsClinicianNotes({
      padNotes: storedPadGoalsNotes,
      mdtPlanNotes: mdtPlanGoalsNotes,
    });
    if (resolvedGoals.source === 'mdtPlan') {
      initialNotes['2'] = resolvedGoals.value;
      setShowMdtPlanPrefillBanner(true);
      mdtGoalsHydratedThisSession.current = true;
      if (canEdit) {
        updatePatientFirestore(patientId, {
          '5_postAssessmentDiscussion': {
            sections: {
              [currentSectionNumber]: {
                subsections: {
                  '2': { clinicianNotes: resolvedGoals.value },
                },
              },
            },
          },
        });
        // Treat the copy as a real input change so section completion and
        // unmanaged exits regenerate the goals paragraph (including patients
        // whose first generation ran against an empty PAD field).
        setHasChanges(true);
      }
    }

    setFormState(initialFormState);
    setNotes(initialNotes);
    notesRef.current = initialNotes;

    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData, patientDataModel, canEdit, patientId, updatePatientFirestore]); 

    // Check if any required notes are empty
    const hasEmptyRequiredNotes = useMemo(() => {
      return Object.entries(subsections).some(([subsectionId, subsection]) => {
        // Skip if subsection is null, undefined, or shouldn't be displayed
        if (!subsection || !ShouldDisplayElement(subsection, patientDataModel)) {
          return false;
        }
  
        // Check if notes are required and empty
        return subsection.notesRequired === true &&
          (!notes[subsectionId] || notes[subsectionId].trim() === '');
      });
    }, [subsections, notes, patientDataModel]);
  


  const handleMultipleSelect = async (subsectionId, fieldName, option, isSelected) => {
    if (!canEdit) return;
    try {
      // Get current selections
      const currentSelections = formState[fieldName]?.value || [];
      let newSelections;

      if (isSelected) {
        // Remove the option if it's already selected
        newSelections = currentSelections.filter(opt => opt !== option);
      } else {
        // Add the new option
        newSelections = [...currentSelections, option];
      }

      // Create update object
      const updateObject = {
        value: newSelections
      };

      // Update local state first
      setFormState(prev => ({
        ...prev,
        [fieldName]: updateObject
      }));

      // Find the question index in the subsection
      const questionIndex = Object.entries(sectionData.subsections[subsectionId].questions)
        .find(([_, q]) => q.firestoreFieldName === fieldName)?.[0];

      if (!questionIndex) {
        throw new Error('Question not found');
      }

      // Uses nested-object format (matching CI/SSI convention) so the write
      // can be deep-merged with other section writes when batched together.
      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  questions: {
                    [questionIndex]: { answer: updateObject }
                  }
                }
              }
            }
          }
        }
      });
      setHasChanges(true);
    } catch (error) {
      console.error('Error updating Firestore:', error);
      // Revert local state if Firestore update fails
      setFormState(prev => ({
        ...prev,
        [fieldName]: {
          value: formState[fieldName]?.value || []
        }
      }));
    }
  };

  // Add the notes handlers
  const handleNotesChange = (subsectionId, value) => {
    if (!canEdit) return;
    setNotes(prev => {
      const next = { ...prev, [subsectionId]: value };
      notesRef.current = next;
      return next;
    });
    setHasChanges(true);
    // Dismiss the MDT Plan prefill banner once the clinician edits the goals.
    if (subsectionId === '2' && showMdtPlanPrefillBanner) {
      setShowMdtPlanPrefillBanner(false);
    }
  };

  // Persists the current note value to Firestore on blur.
  //
  // IMPORTANT: this handler intentionally does NOT call setHasChanges(true).
  // Dirty-tracking is handled exclusively by handleNotesChange (the onChange
  // handler). The blur handler's sole job is data persistence. Combining the
  // two caused false-positive dirty checks: the DictationTextArea's unmount
  // safety-net can fire onBlur even when no text was edited, which would
  // incorrectly flag the page as dirty and trigger the navigation guard.
  const persistSubsectionNotes = useCallback((subsectionId, value) => {
    // Patient's Goals: persist '' when cleared so generation can tell
    // "clinician deleted the MDT prefill" from "PAD notes were never written"
    // (null). Other subsections keep the historical null-for-empty write.
    const clinicianNotes = subsectionId === '2'
      ? (value ?? '')
      : (value || null);
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: {
            subsections: {
              [subsectionId]: { clinicianNotes },
            },
          },
        },
      },
    });
  }, [patientId, updatePatientFirestore]);

  const handleNotesBlur = (subsectionId) => {
    if (!canEdit || !subsectionId) return;

    // Skip the write if the note hasn't changed from what Firestore has.
    const currentValue = notes[subsectionId] ?? '';
    const storedValue = subsections[subsectionId]?.clinicianNotes ?? '';
    if (currentValue === storedValue) return;

    try {
      persistSubsectionNotes(subsectionId, currentValue);
    } catch (error) {
      console.error('Error updating Firestore:', error);
    }
  };

  const [isDiagnosisADHD, setIsDiagnosisADHD] = useState(false);

  useEffect(() => {
    setIsDiagnosisADHD(isPadAdhdOutcomePositive(patientDataModel));
  }, [patientDataModel]);

  // ── Autism Follow-Up gating (negative-ADHD path only) ──────────────────────
  // Mirrors the logic on 6_PAD_TOC.js (positive-path Next Steps), but the panel
  // only renders when the diagnosis is negative (when positive, the toggle lives
  // on the Next Steps page). Uses the same case-insensitive match as the backend
  // _compute_autism_referral_key.
  const isAutismEmergingDifferential = useMemo(() => {
    const differentials = getDifferentialDiagnoses(patientDataModel);
    return differentials.some((name) => name.toLowerCase().includes('autism'));
  }, [patientDataModel]);

  // Show the panel on the negative-ADHD path ONLY when differentials decisions
  // are disabled (TOC / Demo Org) AND autism is actually an emerging
  // differential. On the negative path the panel is entirely hidden when autism
  // is not a differential (unlike the positive-path Next Steps page where it
  // stays visible but greyed out for discoverability).
  const showAutismFollowUpPanel = !isDiagnosisADHD && skipDifferentialsDecisions && isAutismEmergingDifferential;
  // On the negative path the panel only renders when active (the show guard
  // already includes the differential check), but we keep a separate flag for
  // clarity and parity with the positive-path pattern.
  const isAutismFollowUpActive = showAutismFollowUpPanel;

  // Seed from the same Firestore path the Next Steps page (6_PAD_TOC.js) uses.
  // Both pages write to sections.6.includeAutismFollowUp so a diagnosis change
  // carries the value seamlessly.
  const savedSection6 = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['6'] || {};
  const [includeAutismFollowUp, setIncludeAutismFollowUp] = useState(
    () => savedSection6.includeAutismFollowUp === true || savedSection6.includeAutismFollowUp === false
      ? savedSection6.includeAutismFollowUp
      : null,
  );

  // Targeted standalone write for the autism follow-up toggle — decoupled from
  // the page's main write paths. The nested-object format matches how other
  // section-6 fields are written on this page (e.g. handleMultipleSelect).
  const handleAutismFollowUpToggle = (value) => {
    if (!canEdit) return;
    const newVal = includeAutismFollowUp === value ? null : value;
    setIncludeAutismFollowUp(newVal);
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: { 6: { includeAutismFollowUp: newVal } },
      },
    });
    setHasChanges(true);
  };

  // Whether the autism follow-up toggle blocks the Finish button. True when the
  // panel is active (autism is an emerging differential + differentials decisions
  // disabled) but the clinician has not yet answered it.
  const isAutismFollowUpBlocking = isAutismFollowUpActive && includeAutismFollowUp === null;

  const SECTIONS_PAD = useMemo(() => {
    // TOC uses a simplified sidebar: "Assessment Outcome" (always) + "Next Steps"
    // (positive diagnosis only, locked until Patient Goals is filled in).
    // When the diagnosis is negative the PAD ends at Assessment Outcome, so
    // the "Next Steps" entry is omitted entirely.
    if (isTOC) {
      const sections = [
        { sectionTitle: 'Assessment Outcome', route: '/proforma/post-assessment-discussion/review-assessment', status: 'in-progress', isUnlocked: true },
      ];
      if (isDiagnosisADHD) {
        // "Next Steps" is locked until Patient Goals (subsection 2 clinicianNotes) has a non-null value
        const patientGoalsNote = subsections?.['2']?.clinicianNotes;
        const isNextStepsUnlocked = !!patientGoalsNote && patientGoalsNote.trim() !== '';
        sections.push({
          sectionTitle: 'Next Steps',
          route: '/proforma/post-assessment-discussion/next-steps',
          status: 'pending',
          isUnlocked: isNextStepsUnlocked,
        });
      }
      return sections;
    }

    // Standard (non-TOC) multi-section sidebar — uses the shared helper to
    // produce a correctly-ordered and unlock-gated sidebar for both
    // strategies-first and treatment-first workflows.
    const isCarryOutHealthChecksNow = readIsCarryOutHealthChecksNow(patientDataModel);

    const isBeginningMedicationNow = isCarryOutHealthChecksNow && patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes("Yes");

    const section1Complete = !hasEmptyRequiredNotes;

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
        section1Complete,
      }),
    );
  }, [patientDataModel, isDiagnosisADHD, hasEmptyRequiredNotes, padStageOrder, isPadTreatmentFirst]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);


  const handleSectionCompletion = useCallback((patientId, currentStageTitle, sectionKey, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(sectionKey) || false;

    // Flush any local notes that never made it to Firestore (untouched MDT
    // prefill, or typed text whose blur did not fire). Generation reads the
    // PAD field, so this write must land before flushPendingUpdates resolves.
    const liveNotes = notesRef.current || {};
    Object.entries(liveNotes).forEach(([subsectionId, value]) => {
      const storedValue = currentSectionData?.subsections?.[subsectionId]?.clinicianNotes ?? '';
      const currentValue = value ?? '';
      if (currentValue !== storedValue) {
        persistSubsectionNotes(subsectionId, currentValue);
      }
    });

    // Determine what work actually needs doing on this exit
    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges
      || !isCurrentSectionGenerated
      || mdtGoalsHydratedThisSession.current;

    // No-op fast path: section already complete, already generated, no edits
    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      // Use sectionKey (the stable Firestore key) not currentSectionTitle, since for TOC
      // the display title ('Assessment Outcome') differs from the storage key ('Review of Assessment').
      TriggerGenerateDocumentSection(patientId, currentStageTitle, sectionKey, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, currentSectionNumber, sendSectionUpdates, flushPendingUpdates, markRegenHandled, persistSubsectionNotes]);

  const [isNavigating, setIsNavigating] = useState(false);

  const handleNextSection = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      navigate(`/patient/${patientId}${nextSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, nextSectionLink, hasChanges, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, hasChanges, handleSectionCompletion]);

  const handleBackNavigation = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, hasChanges, handleSectionCompletion]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  // After a SLaM PAD redo the live proforma looks brand new. Surface a quiet
  // reminder + archive link so clinicians can still open the superseded
  // discussion (the overview / documents "View original" used to vanish once
  // staleness cleared).
  const padRedoRecord =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.redo || null;
  const padRedoArchiveId = padRedoRecord?.lastRedoArchiveId || null;

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={isTOC ? 0 : getPadSidebarIndex(padStageOrder, '1')}
          navigateToSection={navigateToSection}
          isNavigating={isSaving || isNavigating}
        />
        <div className="flex-grow p-8">
          {padRedoArchiveId && (
            <div className="mb-6">
              <PadRedoArchiveBanner
                isVisible
                snapshotPath={`/patient/${patientId}/pad-snapshot/${padRedoArchiveId}`}
                redoFromOutcome={padRedoRecord?.redoFromOutcome || null}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBackNavigation}
              disabled={isSaving || isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {(isSaving || isNavigating)
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {/* Conditionally render either Next Section button or Finish button */}
            {isDiagnosisADHD ? (
               <div>
              <button
                onClick={handleNextSection}
                disabled={(canEdit && hasEmptyRequiredNotes) || isNavigating}
                data-tooltip-id="required-notes-tooltip-top"
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md ml-auto
                    transition-all duration-300 ease-in-out
                    ${(canEdit && hasEmptyRequiredNotes) || isNavigating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-violet-900 text-white hover:bg-violet-800'
                  }`}
              >
                <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                {isNavigating
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
              
              {canEdit && hasEmptyRequiredNotes && (
                <Tooltip
                  id="required-notes-tooltip-top"
                  place="top"
                  className="z-50 text-xs font-semibold"
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '15px',
                    color: '#fff',
                  }}
                >
                  Please provide details about the patient's goals before proceeding
                </Tooltip>
              )}
            </div>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={isSaving || isNavigating || isAutismFollowUpBlocking}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(isSaving || isNavigating || isAutismFollowUpBlocking) ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>

          {/* Section Title — contract-type badge right-aligned opposite the
              heading, matching the Next Steps page. Gated on the clinician's
              org, so it only shows for orgs that categorise by contract type. */}
          <div className="flex justify-between items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
            <PatientContractTypeBadge
              label="Contract"
              organisation={claims?.organisation}
              assessmentType={patientDataModel?.customFields?.assessmentType}
              subContractType={patientDataModel?.customFields?.subContractType}
            />
          </div>

          <DocumentPreviewPanel
            patientId={patientId}
            clinicianId={auth.currentUser?.uid || ''}
          />

          {/* Subsections */}
          {Object.entries(subsections).map(([subsectionId, subsection]) => {
            // Skip if subsection is null or undefined
            if (!subsection) return null;

            if (!ShouldDisplayElement(subsection, patientDataModel)) {
              return null;
            }

            return (
              <div key={subsectionId} className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, padNav.displayNumber)}                
                </h3>

                {/* Assessment goals read-only row — always shown on Patient's Goals
                    so Path 1 (CI Review → PAD) still surfaces intake answers even
                    when Presenting Complaint was never opened. */}
                {subsectionId === '2' && (
                  <div className="mb-6 mt-2 rounded-lg">
                    <div className="text-slate-700 text-base flex text-left items-center mb-2">
                      <div className="w-96 flex items-center gap-2">
                        <span className="font-bold">Assessment goals</span>
                        {assessmentGoalsDisplay.hasPatientSelfReport ? (
                          <p className="text-sm italic text-gray-600">(from Patient Self-Report)</p>
                        ) : (
                          <p className="text-sm italic text-gray-400">(No previous answer given by Patient)</p>
                        )}
                      </div>
                    
                      <div className="flex items-center">
                        <div className="w-96 p-3 border rounded-md bg-gray-100 text-gray-700 cursor-not-allowed whitespace-pre-wrap">
                          <FormattedAnswer
                            rawAnswer={assessmentGoalsDisplay.rawAnswer}
                            displayText={assessmentGoalsDisplay.displayText || "No answer given"}
                          />
                        </div>
                        
                        {/* Source button — only when there is an intake answer to inspect */}
                        {assessmentGoalsDisplay.hasPatientSelfReport && (
                        <Dialog.Root open={openDialogIndex === 0} onOpenChange={(open) => !open && setOpenDialogIndex(null)}>
                          <Dialog.Trigger asChild>
                            <div
                              className="cursor-pointer ml-1 mt-2"
                              onClick={handleOpenRawPrefillDialog}
                            >
                              <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                                <SearchIcon className="h-3 w-3" />
                              </div>
                            </div>
                          </Dialog.Trigger>
                          
                          <Dialog.Portal>
                            <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                            <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
                              <div className="flex justify-between items-center mb-4">
                                <Dialog.Title className="text-base font-semibold text-gray-900">
                                  Patient Form Responses
                                </Dialog.Title>
                                <Dialog.Close asChild>
                                  <button className="p-1 rounded-full hover:bg-gray-100">
                                    <XIcon className="h-5 w-5 text-gray-500" />
                                  </button>
                                </Dialog.Close>
                              </div>

                              <div className="mb-4">
                                <h3 className="font-semibold text-sm mb-2">Patient's Goals</h3>

                                {/* Last updated timestamp */}
                                {rawPrefillData[0]?.lastUpdated && (
                                  <div className="text-xs text-gray-500 mb-3 flex items-center">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Last updated: {formatDateTime(rawPrefillData[0].lastUpdated)}
                                  </div>
                                )}

                                {/* Display the dialog content */}
                                <div className="space-y-2 mt-2">
                                  {rawPrefillData.length > 0 ? (
                                    rawPrefillData.map((item, i) => (
                                      <div key={i} className={`border rounded-md p-3 ${item.hasAnswer
                                          ? 'bg-violet-50 shadow-sm border-violet-200'
                                          : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                                        }`}>
                                        <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>
                                        
                                        {/* Display question title if available */}
                                        {item.questionTitle && (
                                          <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                                        )}
                                        
                                        <div className="text-xs font-medium mb-1.5">{item.question}</div>

                                        {/* Display skip badge if question was skipped */}
                                        {item.wasSkipped && (
                                          <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
                                            <span>Patient clicked</span>
                                            <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                                              {item.skipButtonText}
                                            </span>
                                          </div>
                                        )}

                                        {/* Display the answer — FormattedAnswer renders any otherValue
                                            free text inline in italic curly quotes. */}
                                        <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${item.hasAnswer
                                            ? 'bg-white border-gray-200'
                                            : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                                          }`}>
                                          <FormattedAnswer
                                            rawAnswer={item.rawAnswer}
                                            displayText={rawPrefillData.length === 1 && dialogPrefillText ? dialogPrefillText : item.answer}
                                          />
                                        </div>
                                        
                                        {/* Display available options for selection questions */}
                                        {item.options && item.options.length > 0 && (
                                          <div className="mt-2">
                                            <div className="text-xs text-gray-500 mb-1">Available options:</div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                              {item.options.map((option, optIndex) => {
                                                const isSelected = item.hasAnswer && isOptionSelected(option, item.rawAnswer);
                                                
                                                return (
                                                  <div
                                                    key={optIndex}
                                                    className={`text-xs p-1 rounded ${
                                                      isSelected
                                                        ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                                        : 'bg-gray-50 border border-gray-200 text-gray-500'
                                                    }`}
                                                  >
                                                    {option}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-gray-500 italic">No source data available</p>
                                  )}
                                </div>
                              </div>
                            </Dialog.Content>
                          </Dialog.Portal>
                        </Dialog.Root>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-left text-gray-700">
                {subsection.tooltipText && (
                   subsection.tooltipText                      
                  )}
                </p>
               

                <div className="space-y-6">
                  {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                    // Skip if question is null or undefined
                    if (!question) return null;

                    // Check if question should be displayed based on conditions
                    if (!ShouldDisplayElement(question, patientDataModel)) {
                      return null;
                    }

                    return (
                      <div key={questionId} className="flex items-start">
                        <div className="w-96 flex items-center gap-2">
                          {question.tooltipText ? (
                            <InfoTooltip
                              content={question.tooltipText}
                              label={`Information about ${question.questionTitle}`}
                              maxWidth="300px"
                              className=""
                            />
                          ) : (
                            <div className="w-4 h-4"></div>
                          )}
                          <h4 className="text-left font-semibold text-gray-700">
                            {question.questionTitle}
                          </h4>
                        </div>

                        <div className="ml-5">
                          <ul className="items-center flex flex-row gap-4">
                            {(question.options || []).map((option) => {
                              const currentSelections = formState[question.firestoreFieldName]?.value || [];
                              const isSelected = currentSelections.includes(option);

                              return (
                                <li
                                  key={option}
                                  onClick={() => canEdit && handleMultipleSelect(
                                    subsectionId,
                                    question.firestoreFieldName,
                                    option,
                                    isSelected
                                  )}
                                  className={`
                                    flex-1 p-2 w-64
                                    rounded-lg transition-all duration-300 ease-in-out
                                    text-center
                                    flex flex-row items-center justify-center gap-2
                                    ${!canEdit
                                      ? 'cursor-not-allowed opacity-75'
                                      : 'cursor-pointer group'
                                    }
                                    ${isSelected
                                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                                      : canEdit
                                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                                    }
                                  `}
                                >
                                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                    {isSelected ? (
                                      <CheckIcon className="w-4 h-4" />
                                    ) : (
                                      <PlusIcon className="w-4 h-4" />
                                    )}
                                  </div>
                                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                    {option}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );

                  })}
                </div>

                {/* Clinician Notes Box */}
                {subsection.hasClinicianNotesBox && (
                  <div className="text-left mt-6">
                    <h4 className="text-gray-700 text-sm font-semibold mb-2">
                      {subsection.textBoxSubtitle}
                      {subsection.notesRequired &&
                        <span className="text-red-600 ml-1">*</span>
                      }
                    </h4>

                    {/* MDT Plan prefill banner — shown when goals text was
                        prefilled from the MDT Plan; disappears on first edit */}
                    {subsectionId === '2' && (
                      <div className={`overflow-hidden transition-all duration-400 ease-in-out ${showMdtPlanPrefillBanner ? 'max-h-20 mb-3 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-violet-600 flex-shrink-0" />
                          <p className="text-sm text-violet-800">
                            This text has been prefilled from the MDT Plan. Edit as needed for the patient letter.
                          </p>
                        </div>
                      </div>
                    )}

                    <DictationTextArea
                      ref={(el) => textAreaRefs.current[subsectionId] = el}
                      value={notes[subsectionId] || ''}
                      onChange={(val) => handleNotesChange(subsectionId, val)}
                      onBlur={() => handleNotesBlur(subsectionId)}
                      canEdit={canEdit}
                    />
                  </div>
                )}
              </div>
            )
          })}

          {/* Autism Follow-Up panel (negative-ADHD path only, TOC orgs) ──────
              When differentials decisions are disabled and autism was flagged as
              an emerging differential, the clinician records here whether the
              report should include autism follow-up instructions. On the
              positive-ADHD path this toggle lives on the Next Steps page
              (6_PAD_TOC.js) instead. */}
          {showAutismFollowUpPanel && (
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                {padNav.displayNumber}.2 Autism Follow-Up
              </h3>
              <div className="space-y-4">
                <AutismDecisionCard
                  variant="adhd"
                  question="Include autism follow-up instructions in the report?"
                  value={isAutismFollowUpActive ? includeAutismFollowUp : null}
                  onChange={handleAutismFollowUpToggle}
                  disabled={!canEdit || !isAutismFollowUpActive}
                  inactive={!isAutismFollowUpActive}
                  tooltip={!isAutismFollowUpActive
                    ? 'This only applies when autism is selected as an emerging differential.'
                    : 'When set to Yes, the report will include a boilerplate paragraph advising the patient to seek a follow-up autism assessment.'}
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
            {/* Conditionally render either Next Section button or Finish button */}
            {isDiagnosisADHD ? (
               <div>
              <button
                onClick={handleNextSection}
                disabled={(canEdit && hasEmptyRequiredNotes) || isNavigating}
                data-tooltip-id="required-notes-tooltip-bottom"
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md ml-auto
                    transition-all duration-300 ease-in-out
                    ${(canEdit && hasEmptyRequiredNotes) || isNavigating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-violet-900 text-white hover:bg-violet-800'
                  }`}
              >
                <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                {isNavigating
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
              
              {canEdit && hasEmptyRequiredNotes && (
                <Tooltip
                  id="required-notes-tooltip-bottom"
                  place="top"
                  className="z-50 text-xs font-semibold"
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '15px',
                    color: '#fff',
                  }}
                >
                  Please provide details about the patient's goals before proceeding
                </Tooltip>
              )}
            </div>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={isSaving || isNavigating || isAutismFollowUpBlocking}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(isSaving || isNavigating || isAutismFollowUpBlocking) ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving
                    ? <ButtonSpinner className="w-5 h-5 ml-2" />
                    : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>

        </div>
        <ModuleCompletionDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          title="Post-Assessment Discussion Complete"
          width={600}
          actions={
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              <GenerateReportButton onClick={handleGenerateReport} />
            </>
          }
        >
          <SummarySection title="Summary:">
            The Post-Assessment Discussion has been completed. Would you like to generate the report?
          </SummarySection>
        </ModuleCompletionDialog>

        <GenerateReportProgressDialog
          reportStage="Post-Assessment Discussion"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
        />
      </div>


    </Layout>
  );
}